import {
  Button,
  ColumnTable,
  DetailBox,
  DetailBoxColumn,
  DisplayInfo,
  Icon,
  Modal,
  Table,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { ReactElement, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import Loading from '../../../../components/info/Loading';
import { moneyFormatter } from '../../../../utils/currency';
import { getAgreedPaymentDetail } from '../api';
import { AgreedPaymentDetail, AgreedPaymentDetailPaidDebt } from '../types';

const dictPrefix = 'financialPortal.agreedPayments.detailModal';

interface Props {
  debtId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const formatDate = (value: unknown): string => {
  if (!value) {
    return '-';
  }
  const d = dayjs(value as string);
  return d.isValid() ? d.utc().format('DD/MM/YYYY') : '-';
};

const formatAmount = (value: number | null | undefined): string =>
  value != null ? moneyFormatter().format(Math.ceil(value)) : '-';

/**
 * La pasarela de un abono llega como `gatewayCode` ('webpay-plus', 'iceberg',
 * …) o como 'discount' para las devoluciones, así que se busca primero en el
 * diccionario de pasarelas y después en el de orígenes.
 */
const translatePaymentChannel = (t: TFunction, value: unknown): string => {
  if (value == null || value === '') {
    return '-';
  }
  return t(
    [
      `paymentHub.paymentsTable.gateways.${value}`,
      `paymentHub.paymentsTable.origins.${value}`,
    ],
    { defaultValue: String(value) },
  );
};

export function AgreedPaymentDetailModal({
  debtId,
  isOpen,
  onClose,
}: Props): ReactElement {
  const { t } = useTranslation();
  const [data, setData] = useState<AgreedPaymentDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!debtId) {
      setData(null);
      return;
    }
    let mounted = true;
    setLoading(true);
    getAgreedPaymentDetail(debtId).then((res) => {
      if (!mounted) {
        return;
      }
      setData(res?.data ?? null);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [debtId]);

  const headerColumns: DetailBoxColumn[] = data
    ? [
        {
          // TODO: XD HU-15461 shows "Estudiante" as two lines (name on line 1,
          // ID on line 2). DetailBox.body is typed `string` and the global-ui
          // implementation wraps it in a `text-overflow-ellipsis` div, so
          // injecting a newline + `white-space: pre-line` will be clipped.
          // Sibling PaymentHistoryDetailModal accepts the same single-line
          // collapse — keeping consistent until global-ui exposes a richer
          // body slot.
          title: t(`${dictPrefix}.header.student`),
          body: `${data.header.student.fullname} - ID ${data.header.student.identificationDisplay}`,
        },
        {
          title: t(`${dictPrefix}.header.concept`),
          body: data.header.concept,
        },
        {
          title: t(`${dictPrefix}.header.debtId`),
          body: data.header.debtIdToDisplay ?? '-',
        },
        {
          title: t(`${dictPrefix}.header.year`),
          body: data.header.year != null ? String(data.header.year) : '-',
        },
        {
          title: t(`${dictPrefix}.header.matriculationDate`),
          body: formatDate(data.header.matriculationDate),
        },
        {
          title: t(`${dictPrefix}.header.contract`),
          body: data.header.contract ?? '-',
        },
        {
          title: t(`${dictPrefix}.header.dueDate`),
          body: formatDate(data.header.installmentDueDate),
        },
        // Bug #16500 — sin estos tres montos la pantalla no dejaba ver de
        // cuánto era la deuda ni si quedaba saldo: había que sumar a mano las
        // filas de la tabla de abonos.
        {
          title: t(`${dictPrefix}.header.debtAmount`),
          body: formatAmount(data.header.debtAmount),
        },
        {
          title: t(`${dictPrefix}.header.totalPaid`),
          body: formatAmount(data.header.totalPaid),
        },
        {
          title: t(`${dictPrefix}.header.balance`),
          body: formatAmount(data.header.balance),
        },
      ]
    : [];

  const paidDebtsColumns: ColumnTable<AgreedPaymentDetailPaidDebt>[] = [
    {
      columnName: 'sustainer',
      headerText: t(`${dictPrefix}.paidDebts.sustainer`),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ value }) => (value as string | null) ?? '-',
    },
    {
      columnName: 'paymentDate',
      headerText: t(`${dictPrefix}.paidDebts.paymentDate`),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ value }) => formatDate(value),
    },
    {
      columnName: 'paymentChannel',
      headerText: t(`${dictPrefix}.paidDebts.paymentChannel`),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ value }) => translatePaymentChannel(t, value),
    },
    {
      columnName: 'authorizationCode',
      headerText: t(`${dictPrefix}.paidDebts.authorizationCode`),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ value }) => (value as string | null) ?? '-',
    },
    {
      columnName: 'voucherId',
      headerText: t(`${dictPrefix}.paidDebts.voucherId`),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ value }) => (value as string | null) ?? '-',
    },
    {
      columnName: 'amount',
      headerText: t(`${dictPrefix}.paidDebts.amount`),
      tdClassName: 'text-right',
      thClassName: 'text-right',
      cellFormat: ({ value, row }) => (
        <span className="d-inline-flex align-items-center justify-content-end">
          {moneyFormatter().format(Math.ceil(Number(value ?? 0)))}
          {row.attachmentUrl && (
            <a
              href={row.attachmentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 d-inline-flex align-items-center"
              title={t(`${dictPrefix}.paidDebts.downloadAttachment`)}
            >
              <Icon name="download" size="16px" color="primary" />
            </a>
          )}
        </span>
      ),
    },
  ];

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <Container>
        {loading && <Loading insideCard />}
        {!loading && data && (
          <>
            <Row>
              <Col xs={12} className="text-center mb-3">
                <Icon name="information" size="40px" color="primary" />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <h1 className="text-dark fs-22 text-center mb-4">
                  {t(`${dictPrefix}.title`)}
                </h1>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col xs={12}>
                <DetailBox columns={headerColumns} />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <h5 className="text-uppercase text-primary fw-700 mb-3">
                  {t(`${dictPrefix}.paidDebtsSectionTitle`)}
                </h5>
                <Table
                  data={data.paidDebts}
                  columns={paidDebtsColumns}
                  noResultsText={
                    <div className="d-flex justify-content-center align-items-center py-4">
                      <DisplayInfo
                        iconName="information"
                        textBody={t(`${dictPrefix}.paidDebts.noResults`)}
                        maxWidth="500px"
                      />
                    </div>
                  }
                />
              </Col>
            </Row>
          </>
        )}
        <Row className="mt-4">
          <Col xs={12} className="d-flex justify-content-end">
            <Button
              type="button"
              size="md"
              text={t(`${dictPrefix}.close`)}
              onClick={onClose}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
}
